<template>
  <div
    v-if="enabled"
    class="reticle"
    :class="{ 'reticle-armed': reduced && !tracking }"
    aria-hidden="true"
  >
    <span ref="crossX" class="reticle-line-x"></span>
    <span ref="crossY" class="reticle-line-y"></span>
    <span ref="boxEl" class="reticle-box"><span class="reticle-box-inner"></span></span>
    <span ref="coordsEl" class="reticle-coords">X 0000 Y 0000 TRK</span>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

/*
 * Page-level targeting axis. Tracks the pointer across the whole viewport and
 * drifts between waypoints when the pointer is idle, so the HUD reads as
 * running rather than as a hover effect bolted to one component.
 *
 * Positions live in plain closure variables, not reactive state: this writes to
 * style.transform every frame, and making that reactive would re-render the
 * component 60 times a second for nothing.
 *
 * Under prefers-reduced-motion the axis stays, but every autonomous part of it
 * goes: no idle drift to random waypoints, no eased trailing, no animation
 * frame loop at all. What is left is a direct read of the pointer written on
 * the pointermove event, which is user-driven rather than motion the page
 * inflicts on its own. It stays hidden until the first real pointer move so it
 * never appears parked in the middle of the screen.
 */

const IDLE_MS = 1800; // pointer treated as idle after this
const LERP = 0.075;   // easing toward the target
const HALF_BOX = 32;  // half the bracket box, matches the CSS size

export default defineComponent({
  name: 'NervReticle',
  setup() {
    const enabled = ref(false);
    const reduced = ref(false);
    const tracking = ref(false); // reduced mode: armed by the first pointer move
    const crossX = ref<HTMLElement | null>(null);
    const crossY = ref<HTMLElement | null>(null);
    const boxEl = ref<HTMLElement | null>(null);
    const coordsEl = ref<HTMLElement | null>(null);

    let x = 0, y = 0;          // current, eased
    let tx = 0, ty = 0;        // target
    let px = 0, py = 0;        // last pointer position
    let pointerAt = -1e9;
    let nextWaypointAt = 0;
    let raf = 0;
    let shown = '';

    function onPointer(e: PointerEvent) {
      // Coarse pointers fire pointermove on tap; those should not drive a HUD.
      if (e.pointerType === 'touch') return;
      px = e.clientX;
      py = e.clientY;
      pointerAt = performance.now();
      if (reduced.value) {
        // No loop is running, so this event is the only thing that paints.
        tracking.value = true;
        paint(Math.round(px), Math.round(py));
      }
    }

    function pickWaypoint(now: number) {
      tx = window.innerWidth * (0.1 + Math.random() * 0.8);
      ty = window.innerHeight * (0.12 + Math.random() * 0.76);
      nextWaypointAt = now + 2600 + Math.random() * 2400;
    }

    function paint(rx: number, ry: number) {
      if (crossX.value) crossX.value.style.transform = `translateY(${ry}px)`;
      if (crossY.value) crossY.value.style.transform = `translateX(${rx}px)`;
      if (boxEl.value) {
        boxEl.value.style.transform = `translate(${rx - HALF_BOX}px, ${ry - HALF_BOX}px)`;
      }
      if (coordsEl.value) {
        const text = `X ${String(rx).padStart(4, '0')} Y ${String(ry).padStart(4, '0')} TRK`;
        if (text !== shown) {
          shown = text;
          coordsEl.value.textContent = text;
        }
        // Flip the tag back across the axis near the right and bottom edges so
        // it never runs off screen.
        const flipX = rx > window.innerWidth - 180;
        const flipY = ry > window.innerHeight - 80;
        coordsEl.value.style.transform =
          `translate(${rx + (flipX ? -168 : 42)}px, ${ry + (flipY ? -46 : 42)}px)`;
      }
    }

    function frame(now: number) {
      if (now - pointerAt < IDLE_MS) {
        tx = px;
        ty = py;
        nextWaypointAt = now + 1200;
      } else if (now >= nextWaypointAt) {
        pickWaypoint(now);
      }

      x += (tx - x) * LERP;
      y += (ty - y) * LERP;

      paint(Math.round(x), Math.round(y));
      raf = requestAnimationFrame(frame);
    }

    function start() {
      window.addEventListener('pointermove', onPointer, { passive: true });
      if (reduced.value) {
        // Event-driven only. Nothing moves until the pointer does.
        tracking.value = false;
        return;
      }
      x = window.innerWidth / 2;
      y = window.innerHeight / 2;
      pickWaypoint(performance.now());
      raf = requestAnimationFrame(frame);
    }

    function stop() {
      cancelAnimationFrame(raf);
      raf = 0;
      window.removeEventListener('pointermove', onPointer);
    }

    // A fine pointer and a wide enough viewport. Motion preference no longer
    // gates the axis, it selects which of the two modes runs.
    function evaluate() {
      const mm = typeof window.matchMedia === 'function';
      const ok =
        mm &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
        window.innerWidth > 820;
      const wantsReduced = mm && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (ok === enabled.value && wantsReduced === reduced.value) return;

      stop();
      reduced.value = wantsReduced;
      enabled.value = ok;
      if (ok) nextTick(start);
    }

    const motionQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;

    onMounted(() => {
      evaluate();
      window.addEventListener('resize', evaluate);
      motionQuery?.addEventListener('change', evaluate);
    });

    onBeforeUnmount(() => {
      stop();
      window.removeEventListener('resize', evaluate);
      motionQuery?.removeEventListener('change', evaluate);
    });

    return { enabled, reduced, tracking, crossX, crossY, boxEl, coordsEl };
  },
});
</script>

<style scoped>
.reticle {
  position: fixed;
  inset: 0;
  /* Over the sticky top bar (60) so the axis crosses the full viewport with no
     dead band at the top. Still under the scanline overlay (9999). */
  z-index: 70;
  pointer-events: none;
  opacity: 0.5;
}

/* Reduced motion, before the pointer has moved: mounted and wired, but not yet
   drawn anywhere, so it never sits parked mid-screen. */
.reticle-armed {
  visibility: hidden;
}

.reticle-line-x,
.reticle-line-y {
  position: absolute;
  will-change: transform;
}

.reticle-line-x {
  left: 0;
  top: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 153, 0, 0.45), transparent);
}

.reticle-line-y {
  top: 0;
  left: 0;
  width: 1px;
  height: 100%;
  background: linear-gradient(180deg, transparent, rgba(255, 153, 0, 0.45), transparent);
}

.reticle-box {
  position: absolute;
  top: 0;
  left: 0;
  width: 64px;
  height: 64px;
  will-change: transform;
}

.reticle-box::before,
.reticle-box::after,
.reticle-box-inner::before,
.reticle-box-inner::after {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
  border: 0 solid var(--nerv-orange);
  opacity: 0.85;
}

.reticle-box::before { top: 0; left: 0; border-width: 1px 0 0 1px; }
.reticle-box::after { bottom: 0; right: 0; border-width: 0 1px 1px 0; }

.reticle-box-inner {
  position: absolute;
  inset: 0;
}

.reticle-box-inner::before { top: 0; right: 0; border-width: 1px 1px 0 0; }
.reticle-box-inner::after { bottom: 0; left: 0; border-width: 0 0 1px 1px; }

.reticle-coords {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0.1rem 0.3rem;
  background: rgba(0, 0, 0, 0.72);
  box-shadow: 0 0 0 1px rgba(255, 153, 0, 0.18);
  color: var(--nerv-orange);
  font-family: var(--font-nerv-mono);
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  will-change: transform;
}
</style>
